import { DOCUMENT, isPlatformBrowser } from '@angular/common';
import { DestroyRef, Directive, ElementRef, NgZone, PLATFORM_ID, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { NzPopconfirmDirective } from 'ng-zorro-antd/popconfirm';
import { NzTooltipDirective } from 'ng-zorro-antd/tooltip';
import { fromEvent, merge } from 'rxjs';

type ScrollClosableOverlay = NzTooltipDirective | NzPopconfirmDirective;

@Directive({
  selector: '[appHideTooltipOnScroll]',
  standalone: true
})
export class HideTooltipOnScrollDirective {
  private readonly document = inject(DOCUMENT);
  private readonly platformId = inject(PLATFORM_ID);
  private readonly zone = inject(NgZone);
  private readonly destroyRef = inject(DestroyRef);
  private readonly tooltip = inject(NzTooltipDirective, { optional: true, self: true });
  private readonly popconfirm = inject(NzPopconfirmDirective, { optional: true, self: true });
  private readonly overlay = this.tooltip ?? this.popconfirm;
  private readonly elementRef = inject<ElementRef<HTMLElement>>(ElementRef);
  private isOverlayVisible = false;
  private wasHiddenByScroll = false;

  constructor() {
    const overlay = this.overlay;

    if (!isPlatformBrowser(this.platformId) || !overlay) {
      return;
    }

    const eventOptions: AddEventListenerOptions = { capture: true, passive: true };
    const window = this.document.defaultView;
    const scrollEvents = [
      fromEvent(this.document, 'scroll', eventOptions),
      fromEvent(this.document, 'wheel', eventOptions),
      fromEvent(this.document, 'touchmove', eventOptions)
    ];

    if (window) {
      scrollEvents.push(fromEvent(window, 'scroll', eventOptions));
      scrollEvents.push(
        ...this.getScrollableAncestors(window).map(scrollable => fromEvent(scrollable, 'scroll', eventOptions))
      );
    }

    this.zone.runOutsideAngular(() => {
      overlay.visibleChange.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(isVisible => {
        this.isOverlayVisible = isVisible;

        if (isVisible) {
          this.wasHiddenByScroll = false;
        }
      });

      merge(...scrollEvents)
        .pipe(takeUntilDestroyed(this.destroyRef))
        .subscribe(() => this.hideOverlayOnScroll(overlay));

      fromEvent(this.elementRef.nativeElement, 'click')
        .pipe(takeUntilDestroyed(this.destroyRef))
        .subscribe(() => {
          if (!this.wasHiddenByScroll) {
            return;
          }

          this.wasHiddenByScroll = false;
          this.zone.run(() => overlay.show());
        });
    });
  }

  private hideOverlayOnScroll(overlay: ScrollClosableOverlay): void {
    if (!this.isOverlayVisible) {
      return;
    }

    this.isOverlayVisible = false;
    this.wasHiddenByScroll = true;
    this.zone.run(() => overlay.hide());
  }

  private getScrollableAncestors(window: Window): HTMLElement[] {
    const scrollableAncestors: HTMLElement[] = [];
    let ancestor = this.elementRef.nativeElement.parentElement;

    while (ancestor) {
      if (this.isScrollableElement(ancestor, window)) {
        scrollableAncestors.push(ancestor);
      }

      ancestor = ancestor.parentElement;
    }

    return scrollableAncestors;
  }

  private isScrollableElement(element: HTMLElement, window: Window): boolean {
    const style = window.getComputedStyle(element);
    const overflowValues = [style.overflow, style.overflowX, style.overflowY];
    const canScroll = element.scrollHeight > element.clientHeight || element.scrollWidth > element.clientWidth;

    return canScroll && overflowValues.some(value => value === 'auto' || value === 'scroll' || value === 'overlay');
  }
}
